<template>
  <div v-if="propertyData">
    <div
      id="page-container"
      class="h-100"
    >
      <div class="container">
        <div class="row">
          <div class="col-12">
            <div
              class="back"
              @click="goBack()"
            >
              <SvgArrowBack />
              <span class="d-none d-md-inline pl-2">
                Voir tous nos biens en vente
              </span>
            </div>
            <div
              id="pictures-container"
              class="d-none d-lg-block"
            >
              <AdsPicturesGrid
                :pictures="propertyData?.pictures"
                :property-name="`Achat ${propertyData?.type} T${propertyData?.rooms} à ${propertyData?.city?.name} (${propertyData?.city?.zipcode}) - ${propertyData?.area?.value || propertyData?.area?.total}m²`"
                class="mb-5"
                @did-select-picture="didSelectPicture"
              />
              <div
                v-if="stickerText"
                class="sticker-list"
              >
                <div class="label">
                  {{ stickerText }}
                </div>
              </div>
            </div>
            <div
              id="mobile-carousel-container"
              class="d-xs-block d-sm-block d-lg-none"
            >
              <AdsCarousel
                :active-index="activePictureIndex"
                :pictures="propertyData?.pictures"
                :desktop="false"
              />
              <div
                v-if="stickerText"
                class="sticker-list"
              >
                <div class="label">
                  {{ stickerText }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="container">
        <div class="row">
          <div class="col-12 col-lg-8">
            <AdsDescription :property-data="propertyData" />
            <div class="row">
              <div class="col-12 col-lg-10 offset-lg-1">
                <AdsBottomContactBlock :property-data="propertyData" />
              </div>
            </div>
          </div>
          <div class="col-12 col-lg-4">
            <AdsStickyContactBlock :property-data="propertyData" />
          </div>
        </div>
      </div>
      <AdsStickyBar />
      <AdsModalCarousel
        v-if="showModal == true"
        :active-index="activePictureIndex"
        :pictures="propertyData?.pictures"
        @closemodal="closeModalCarousel"
      />
    </div>
    <AdsSiblingAnnonces
      v-if="siblingsProperties.length"
      :properties="siblingsProperties"
    />
    <AdsOurAdvice
      v-if="advices"
      :advices="advices ?? []"
      class="section-padding--bottom"
    />
  </div>
</template>

<script lang="ts" setup>
const route = useRoute()
const router = useRouter()

const showModal = ref(false)
const activePictureIndex = ref(0)
const config = useRuntimeConfig()

const t = await useAsyncData(
  'multipleData',
  async () => {
    let id = route.path.split('-').pop()
    let provider = 'apimo'
    let _propertyData: any = {}

    if (id) {
      if (id.startsWith('r')) {
        id = id.replace('r', '')
        provider = 'reselling'
      }

      if (!parseInt(id)) {
        throw createError({ statusCode: 404, message: 'Post not found' })
      }
    } else {
      throw createError({ statusCode: 404, message: 'Post not found' })
    }

    // Property data
    try {
      switch (provider) {
        case 'reselling':
          _propertyData = await getService('resellings-infos').get(id)
          _propertyData.isReselling = true
          break

        default:
          _propertyData = await useFetch('/apimo/properties/' + id, { baseURL: config.app.apiUrl })
          _propertyData = _propertyData.data.value
          break
      }
    } catch (e: any) {
      console.log(e.response)
    }

    if (!_propertyData) {
      throw createError({ statusCode: 404, message: 'Post not found' })
    }

    if (_propertyData.removed) {
      let region: any = null
      switch (_propertyData.lead_owner?.branch?.name) {
        case 'Paris':
          region = 'paris-ile-de-france'
          break
        default:
          region = _propertyData.lead_owner?.branch?.name?.toLowerCase()
      }
      if (region) {
        navigateTo(`/acheter/annonces/region/${region}`, { redirectCode: 301 })
      }
    }

    if ('value' in (_propertyData.medias?.[0] ?? {})) {
      const media = _propertyData.medias[0]
      _propertyData.pictures?.splice(5, 0, media)
    }

    const { data: fetchedAdvices } = await useFetch(`/blog/posts${_propertyData.value?.lead_owner?.branch?.name ? `?city=${_propertyData.value?.lead_owner?.branch?.name}` : ''}`, { baseURL: config.app.apiUrl })
    const _advices = fetchedAdvices.value as any[]

    // Sibling properties
    function distance (lat1: number, lon1: number, lat2: number, lon2: number) {
      const p = 0.017453292519943295
      const c = Math.cos
      const a = 0.5 - c((lat2 - lat1) * p) / 2 +
        c(lat1 * p) * c(lat2 * p) *
        (1 - c((lon2 - lon1) * p)) / 2
      return 12742 * Math.asin(Math.sqrt(a))
    }

    let _siblingsProperties: any
    try {
      const { data: properties } = await useFetch('/apimo/properties', { baseURL: config.app.apiUrl })
      const isSiblingsButNotTheCurrentOne = (property: any) => property?.lead_owner?.branch && _propertyData?.lead_owner?.branch && (property?.lead_owner?.branch?.name === _propertyData?.lead_owner?.branch?.name && _propertyData?.reference !== property?.reference)
      _siblingsProperties = (properties.value as any[]).filter(isSiblingsButNotTheCurrentOne)
      _siblingsProperties = _siblingsProperties.sort(function (a: any, b: any) {
        return distance(_propertyData?.latitude, _propertyData?.longitude, a.location.lat, a.location.lon) - distance(_propertyData?.latitude, _propertyData?.longitude, b.location.lat, b.location.lon)
      })
      if (_siblingsProperties.length > 12) { _siblingsProperties.length = 12 }
    } catch (err) {
      console.warn(err)
    }

    return { propertyData: _propertyData, advices: _advices, siblingsProperties: _siblingsProperties }
  }
)

const propertyData = t.data.value?.propertyData
const advices = t.data.value?.advices
const siblingsProperties = t.data.value?.siblingsProperties

useSeoMeta({
  description: `Achat ${propertyData.type} T${propertyData.rooms} à ${propertyData.city.name} (${propertyData.city?.zipcode}) - ${propertyData.area?.value || propertyData.area?.total}m²`,
  ogDescription: propertyData.comments?.[0].comment,
  ogImage: propertyData.pictures?.[0].url,
  twitterCard: 'summary_large_image'
})

useHead({
  bodyAttrs: {
    class: 'v2023'
  },
  title: `Achat ${propertyData.type} T${propertyData.rooms} à ${propertyData.city.name} (${propertyData.city?.zipcode}) - ${propertyData.area?.value || propertyData.area?.total}m²`,
  meta: [
    {
      hid: 'og:title',
      property: 'og:title',
      content: `Achat ${propertyData.type} T${propertyData.rooms} à ${propertyData.city.name} (${propertyData.city?.zipcode}) - ${propertyData.area?.value || propertyData.area?.total}m²`
    },
    {
      hid: 'description',
      name: 'description',
      content: propertyData.comments?.[0].comment

    },
    {
      hid: 'og:description',
      property: 'og:description',
      content: propertyData.comments?.[0].comment

    },
    {
      hid: 'og:image',
      property: 'og:image',
      content: propertyData.pictures?.[0].url
    },
    {
      hid: 'twitter:title',
      property: 'twitter:title',
      content: `Achat ${propertyData.type} T${propertyData.rooms} à ${propertyData.city.name} (${propertyData.city?.zipcode}) - ${propertyData.area?.value || propertyData.area?.total}m²`
    },
    {
      hid: 'twitter:description',
      property: 'twitter:description',
      content: propertyData.comments?.[0].comment

    },
    {
      hid: 'twitter:image',
      property: 'twitter:image',
      content: propertyData.pictures?.[0].url
    }
  ]

})

onMounted(() => {
  const param = route.params.param
  if (param === 'fr') {
    router.push({
      path: '/acheter/annonces'
    })
  }
})

const stickerText = computed(() => {
  if (propertyData.value?.preselling &&
    !propertyData.value?.offer &&
    !propertyData.value?.precontract &&
    !propertyData.value?.option) {
    return 'AVANT-PREMIÈRE'
  } else if (propertyData.value?.offer) {
    return 'SOUS OFFRE'
  } else if (propertyData.value?.precontract) {
    return 'SOUS COMPROMIS'
  } else if (propertyData.value?.option && !propertyData.value?.off_market) {
    return 'SOUS OPTION'
  }
  return null
})

function didSelectPicture (index: number) {
  activePictureIndex.value = index
  showModal.value = true
  lockScroll(true)
}

function lockScroll (val: boolean) {
  const position = val ? 'fixed' : 'relative'
  document.body.style.position = position
}

function closeModalCarousel () {
  showModal.value = false
  lockScroll(false)
}

function goBack () {
  const oldURL = document.referrer
  if (oldURL.length === 0) {
    document.location.href = '/acheter/annonces'
  } else {
    document.location.href = oldURL
  }
}
</script>

<style lang="scss" scoped>
.h-100 {
  margin-bottom: 40px;
  font-weight: 400;
}

.header {
  height: 80px;
  width: 100%;
  text-align: center;

  svg {
    width: 210px;
    height: auto;
    fill: $purple;
    margin-top: 15px;
  }
}

$green: #2FAF85;

:deep(.section-title) {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 26px;
  margin-bottom: 30px;
}

:deep(.toggle) {
  float: right;
}

.back {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: normal;
  text-decoration: none;
  color: $night-blue;
  cursor: pointer;
  padding: 15px;
}

@media (width >= 768px) {
  #page-container {
    padding-left: 20px;
    padding-right: 20px;
  }
}

@media (width <= 768px) {
  .back {
    top: 0;

    .arrow {
      font-size: 32px;
    }
  }
}

#pictures-container {
  position: relative;
  cursor: pointer;
}

.label {
  background: $purple;
  text-align: center;
  color: #fff;
  font-size: 13px;
  border: 2px solid #fff;
  font-weight: bold;
  padding: 10px 25px;
  border-radius: 25px;
}

.label-mobile {
  background: $purple;
  text-align: center;
  color: #fff;
  font-size: 13px;
  border: 2px solid #fff;
  font-weight: bold;
  padding: 10px 25px;
  border-radius: 25px;
}

#mobile-carousel-container {
  max-width: 100%;
  overflow: hidden;
}

.sticker-list {
  display: flex;
  flex-flow: row wrap;
  position: absolute;
  left: 45px;
  bottom: -18px;
  gap: 5px;

    @include media-breakpoint-down(sm) {
      position: absolute;
      left: 10px;
      top: 310px;
      bottom: unset;
    }
}
</style>
